import type { OfflineScene } from "@novorender/api";

import { log } from "utils/log";

const l = log.miniMap.tiled;

/** Subfolder tiled minimap files live under, inside the SDK's OPFS scene directory. */
const MINIMAP_DIR_NAME = "minimap";

/**
 * Journal-unsafe characters, mirroring the SDK's own `isJournalSafeName`: the
 * journal is unescaped `name,size` text, so a comma or control character in a
 * stored name corrupts every entry after it.
 */
function hasJournalUnsafeChars(name: string): boolean {
    for (let i = 0; i < name.length; i++) {
        const code = name.charCodeAt(i);
        if (code <= 0x1f || name[i] === ",") {
            return true;
        }
    }
    return false;
}

type MinimapOfflineContext = {
    /**
     * Lazy on purpose: incremental caching arms (`addScene`) after this
     * registry is set up, so the scene may not exist yet at registration time.
     */
    getScene: () => OfflineScene | undefined;
    /** Whether OPFS hits/misses may be teed into new writes this session. */
    writeEnabled: boolean;
    /** Whether OPFS may be read at all (covers fully-synced scenes even when writes are kill-switched). */
    readEnabled: boolean;
};

let context: MinimapOfflineContext | undefined;

/** Arm the tiled minimap route's OPFS read-through. `undefined` = network-only, matching pre-offline behavior. */
export function registerMinimapOfflineStore(ctx: MinimapOfflineContext): void {
    context = ctx;
}

export function unregisterMinimapOfflineStore(): void {
    context = undefined;
}

/** Test-only: replace/inspect the active registration without importing the effect that normally sets it. */
export function getMinimapOfflineContext(): MinimapOfflineContext | undefined {
    return context;
}

/** Test-only: reset the pending-write backlog between tests. */
export function resetMinimapOfflineTestState(): void {
    pendingWrites.clear();
}

/**
 * Writes that arrived before `context.getScene()` had a scene to write into
 * yet (e.g. a cold Procore load: mini map fetches can start before the
 * sibling `incrementalSync` effect's `addScene` resolves), keyed by OPFS
 * path so a tile requested again before the scene appears only queues once.
 * Flushed opportunistically the next time a request finds a scene -- without
 * this, a file fetched during that window is silently never written, since
 * `TileBitmapCache`/`fetchPreviewBitmap` cache the decoded bitmap in memory
 * and never fetch that URL again this session.
 *
 * This is the one piece `OfflineScene.requestFile` (BM-14017) doesn't cover:
 * it needs an actual scene to call it on, and there is deliberately no such
 * thing yet during this window.
 */
const pendingWrites = new Map<string, ArrayBuffer>();
/** Bound the backlog in case the scene never appears this session at all. */
const MAX_PENDING_WRITES = 64;

function queuePendingWrite(path: string, buffer: ArrayBuffer): void {
    if (!pendingWrites.has(path) && pendingWrites.size >= MAX_PENDING_WRITES) {
        const oldestPath = pendingWrites.keys().next().value as string;
        pendingWrites.delete(oldestPath);
    }
    pendingWrites.set(path, buffer);
}

/**
 * Flush the backlog directly through `scene.dir` rather than
 * `scene.requestFile`: these bytes are already in hand (fetched while there
 * was nowhere to write them), so there is nothing to read-through or fetch,
 * just a write to report afterward. Best-effort like everything else here --
 * a failure (including a `QuotaExceededError`, which this path has no latch
 * for) is logged and moves on; a regular tile write going through
 * `requestMinimapFile` right after would hit the SDK's own latch regardless.
 */
function flushPendingWrites(scene: OfflineScene): void {
    if (pendingWrites.size === 0) {
        return;
    }
    const toFlush = [...pendingWrites];
    pendingWrites.clear();
    for (const [path, buffer] of toFlush) {
        const size = buffer.byteLength;
        void scene.dir.write(path, buffer).then(
            () => scene.reportOfflineFileAccess(path, size, { retain: true }),
            (error: unknown) => l.debug("Failed to flush queued minimap write:", path, error),
        );
    }
}

/**
 * The path a tile-set file is journaled under inside the scene directory, or
 * `undefined` when it can't be stored safely.
 * @remarks
 * Relative paths come from the server's manifest (`tileUrlTemplate`, preview
 * `path`), so they are treated as untrusted text: a `..` segment could escape
 * the minimap subfolder, and a comma or control character would corrupt the
 * SDK's journal for every entry after it. Rejecting costs one file (network
 * fetch still works, just uncached); accepting could cost the journal.
 *
 * Deliberately not keyed by `sceneVersionId` on top of `fileInstanceId`:
 * `platformFileInstanceId` is understood to be immutable and unique per PDF
 * mapping on the Model Manager side -- re-mapping a sheet mints a new one
 * rather than regenerating a tile set under an existing id, so there's no
 * version to collide across. (Flagged in review; this understanding hasn't
 * been independently verified against Model Manager's own source, so treat
 * it as the team's current best knowledge, not a guarantee -- ask Model
 * Manager if this ever needs to be load-bearing for correctness elsewhere.)
 */
export function minimapFilePath(fileInstanceId: string, relativePath: string): string | undefined {
    if (!fileInstanceId || hasJournalUnsafeChars(fileInstanceId) || hasJournalUnsafeChars(relativePath)) {
        return undefined;
    }
    if (relativePath.startsWith("/")) {
        return undefined;
    }
    const segments = relativePath.split("/");
    if (segments.some((segment) => segment === "" || segment === "." || segment === "..")) {
        return undefined;
    }
    return `${MINIMAP_DIR_NAME}/${fileInstanceId}/${relativePath}`;
}

/**
 * OPFS-read-through fetch of one tile-set file: a cache hit returns from
 * disk; a miss calls `resolveUrl()` and fetches, teeing a successful result
 * back to disk. Delegates entirely to `OfflineScene.requestFile` (BM-14017,
 * `@novorender/api` 2.3.25) once the offline store is armed for reading --
 * this is the single seam every tile/preview/manifest request flows through.
 *
 * Falls back to a plain network fetch (no OPFS involvement at all) when
 * reading is disabled, `fileInstanceId`/`relativePath` can't be stored
 * safely, or there is no scene yet -- except a write that arrives during
 * that last window (writing is allowed, but no scene exists yet) is queued
 * rather than dropped; see `pendingWrites`.
 *
 * @remarks
 * `readEnabled: false` while `writeEnabled: true` is a combination no
 * current registration produces (the tiled route's own effect always passes
 * `readEnabled: true`) -- if it ever did, a write made in that state would
 * queue and never flush, since flushing only happens on the branch that
 * requires `readEnabled`. Not handled specially: there is nothing to trust
 * this against today, and the two flags have otherwise always been
 * independent by design (`readEnabled` alone covers a fully-synced scene
 * even under the incremental kill switch).
 */
export async function requestMinimapFile(
    fileInstanceId: string,
    relativePath: string,
    resolveUrl: () => string | null | Promise<string | null>,
): Promise<Response | undefined> {
    const path = minimapFilePath(fileInstanceId, relativePath);
    const scene = path && context?.readEnabled ? context.getScene() : undefined;

    if (path && scene) {
        flushPendingWrites(scene);
        return scene.requestFile(path, resolveUrl, { retain: true, write: context?.writeEnabled ?? false });
    }

    const url = await resolveUrl();
    if (!url) {
        return undefined;
    }
    const response = await fetch(url);
    if (path && context?.writeEnabled && response.ok) {
        // Scene not armed yet -- queue instead of dropping.
        void response
            .clone()
            .arrayBuffer()
            .then((buffer) => queuePendingWrite(path, buffer))
            .catch((error: unknown) => l.debug("Failed to queue minimap write:", path, error));
    }
    return response;
}

/** Read a tile-set file straight from OPFS (no fetch attempted), or `undefined` on a miss / when reading is unavailable. */
export async function readMinimapFileOffline(
    fileInstanceId: string,
    relativePath: string,
): Promise<ArrayBuffer | undefined> {
    const response = await requestMinimapFile(fileInstanceId, relativePath, () => null);
    return response ? await response.arrayBuffer() : undefined;
}
